'use client';

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion, useReducedMotion } from 'framer-motion';
import { Eye, Heart, Images, Clapperboard, UserPlus, Crown, Sparkles, ArrowUpRight, UserRound } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { trackEvent } from '@/lib/tracking';
import { toAvatarUrl } from '@/lib/media';
import { normalizeSlug } from '@/lib/seo/slug';
import { computeGap, deriveStanding, type GapInfo } from './standing';

type Row = {
  rank: number;
  personId: number;
  stageName: string | null;
  score: number;
  avatarUrl?: string | null;
  // Backend flag: true when the subject is verified + opted in (named +
  // clickable). Anonymous rows come back with personId 0 and null stageName.
  isIdentified?: boolean;
};

type BoardResponse = {
  axis: string;
  scope: string;
  period: string;
  // REAL snapshot stamp as of api 2026-07-29 (it used to be read time, so a
  // week-old board looked freshly computed). Boards are materialized by a weekly
  // sweep, so stating when they were last recounted is what makes the gap copy
  // honest — a gap here is a standing, not a live counter. Null on empty boards,
  // and absent until the API deploy lands, so every read of it is optional.
  computedAt?: string | null;
  rows: Row[];
};

// `score` has always been in the /me payload (PlacementSummary is a positional
// record and the API serializes camelCase); the client just never declared it.
// It's the key to the whole engagement layer: it's the ONLY way to place an
// anonymous ranker on the board, and anonymous rankers are 100% of the live
// post-likes and gallery-likes boards.
type Placement = { axis: string; scope: string; period: string; rank: number; score?: number };

// comments-received axis hidden 2026-07-08: zero qualifying rows have ever
// existed (13 comments site-wide, none passing the double-KYC filter).
// Backend still computes it; re-add the pill when comment volume exists.
// Labels name the METRIC (not just the content type) so the legend
// ("Ranked by …") and per-row hover units read truthfully. galleries ranks by
// gallery likes, videos by total video views — both all-time (backend axes are
// lifetime cumulative). 'Views' above is profile views; 'Video views' is
// distinct and the two-word label disambiguates them. The icon is scannable
// shorthand for the metric, not decoration.
// Per-axis engagement layer (2026-07-29). Every board used to give the same
// generic advice ("Post your work and earn {unit}") whether the metric was
// profile views — which a creator cannot manufacture at all — or post likes,
// which they can go earn. Each axis now names its own mechanic, its own
// destination, and its own GATE.
//
// climbSub is deliberately STATE-INDEPENDENT: it states the gate as a fact
// rather than detecting the viewer's eligibility. useUploadEligibility can't
// distinguish "not approved" from "request failed" (it defaults to false and
// swallows errors), and its false negative would route an approved creator to an
// onboarding page headed "You're all set". Copy that's true for every reader
// beats a third fetch that misroutes some of them.
//
// browse* points a VIEWER at the surface that actually moves the board. This is
// the only actionable thing on a board where every row is anonymous: the post
// likes and gallery likes boards currently have nothing clickable on them at
// all, because anonymous rows carry no id and cannot be linked.
//
// follows-gained deliberately does NOT point at the follow-suggestions rail:
// /api/follow/me/suggestions returns people the VIEWER can follow, which raises
// the viewer's following count — the opposite side of the metric.
const AXES = [
  {
    value: 'views', label: 'Views', unit: ['view', 'views'], Icon: Eye,
    climbLead: "You're not on the views board yet.",
    climbSub: 'Views are counted when another member opens your profile. A stage name and photo make you easier to find.',
    climbCta: 'Edit your profile', climbHref: '/profile/editprofile',
    browseLabel: 'Browse creators', browseHref: '/search',
    // The only axis whose mechanic isn't self-evident from its label, and the
    // one creators most often assume they can drive directly.
    mechanic: 'A view is counted when another member opens your profile.',
  },
  {
    value: 'posts', label: 'Post likes', unit: ['like', 'likes'], Icon: Heart,
    climbLead: "You're not on the post likes board yet.",
    climbSub: 'Publish a post so members have something of yours to like.',
    // The only one of the five with no approval, KYC or verification gate
    // anywhere in the chain, so it's the safe default for a brand-new member.
    climbCta: 'Write a post', climbHref: '/profile/posts/new',
    browseLabel: 'Read posts', browseHref: '/posts',
  },
  {
    value: 'galleries', label: 'Gallery likes', unit: ['like', 'likes'], Icon: Images,
    climbLead: "You're not on the gallery likes board yet.",
    // The publish condition is stated because it is a SILENT dead end otherwise:
    // the board requires EXISTS(published + approved image), so a gallery whose
    // only photo was held for ID verification is dropped from the board entirely
    // rather than scored 0. The creator completes the action and nothing moves.
    climbSub: "A gallery counts once it has at least one published photo. Photos that need ID verification publish after you're verified.",
    climbCta: 'Create a gallery', climbHref: '/profile/galleries/create',
    browseLabel: 'Browse galleries', browseHref: '/gallery',
  },
  {
    value: 'videos', label: 'Video views', unit: ['view', 'views'], Icon: Clapperboard,
    climbLead: "You're not on the video views board yet.",
    // Accumulation, not proximity: this board's spread is the widest of the five
    // (90 down to 10), so "you're close" would be false here. The honest lever is
    // that ViewCount is a lifetime running total — one video keeps earning.
    climbSub: 'A video keeps earning views long after you upload it. Uploading needs an approved account.',
    climbCta: 'Upload a video', climbHref: '/profile/videos/create',
    browseLabel: 'Watch videos', browseHref: '/videos',
  },
  {
    value: 'follows-gained', label: 'Follows gained', unit: ['follow', 'follows'], Icon: UserPlus,
    climbLead: "You're not on the follows gained board yet.",
    // Names durable prerequisites only. The real ordering mechanic in
    // FollowController.MySuggestions is location match then People.updated DESC,
    // and saving the profile editor bumps `updated` — telling members that would
    // be an invitation to game a discovery surface, so it stays unsaid.
    climbSub: 'Follows come from other members finding you. A complete profile with a photo shows up in more places.',
    climbCta: 'Edit your profile', climbHref: '/profile/editprofile',
    browseLabel: 'Browse creators', browseHref: '/search',
  },
];

// Show a row's gap to the row above only when it's within this many actions.
// A number on EVERY row turns the right edge into what reads as a score column —
// a lone "1" beside a bar reads as "this creator has 1 view", which is exactly
// the misread the no-raw-scores decision exists to prevent. Sparse markers make
// "this one is close" a signal instead of noise, and the sparsity is per-axis
// emergent from live data: on post likes, gallery likes and follows nearly every
// interval qualifies; on views only the bottom few; on video views one.
const GAP_THRESHOLD = 3;

/** "One like behind #4" / "2 likes behind #4" / "Tied at #8". Word-for-one
 *  follows the existing precedent in this file (`'One spot' : '{n} spots'`). */
function gapPhrase(gap: number, aboveRank: number, unit: string[]) {
  if (gap <= 0) return `Tied at #${aboveRank}`;
  return gap === 1 ? `One ${unit[0]} behind #${aboveRank}` : `${gap} ${unit[1]} behind #${aboveRank}`;
}

/** Freshness, derived from the snapshot stamp rather than asserted. Returns null
 *  when the API hasn't sent one (pre-deploy, or an empty board) so the clause is
 *  simply omitted instead of guessed. */
function recountedPhrase(computedAt?: string | null): string | null {
  if (!computedAt) return null;
  const t = Date.parse(computedAt);
  if (Number.isNaN(t)) return null;
  const days = Math.floor((Date.now() - t) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

// Scope + period pickers removed 2026-07-08: city/category scopes and the
// weekly period have no usable data at current traffic; boards are global
// all-time until volume justifies re-adding them.
const SCOPE = 'global';
const PERIOD = 'all-time';

// Brand-ribbon confetti (magenta / coral / gold — never rainbow, so the
// celebration reads as JuicyStarz, not a party-store default).
const CONFETTI_COLORS = ['#F03060', '#F0785A', '#ECBA6A', '#E090E0'];

// Anonymous ranker — no real avatar URL is ever sent (a blurred CSS layer over
// the real photo would still sit un-blurrable in the payload and could out a
// non-consenting creator). A neutral silhouette on a muted fill reads as
// "hidden / unclaimed" at every size and lets the medal / ribbon ring frame it,
// where the old bright-fuchsia blob swallowed the ring at podium scale.
function AnonAvatar() {
  return (
    <span aria-hidden className="flex h-full w-full items-center justify-center bg-muted">
      <UserRound className="h-1/2 w-1/2 text-muted-foreground opacity-40" strokeWidth={1.5} />
    </span>
  );
}

/** Avatar with a graceful fallback — 2/39 rows had broken images on prod. */
function RowAvatar({ src, alt, sizes }: { src: string; alt: string; sizes: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <Image
      src={failed ? '/img/default-avatar.png' : src}
      alt={alt}
      fill
      sizes={sizes}
      className="object-cover"
      onError={() => setFailed(true)}
    />
  );
}

// Medal vocabulary — gold / silver / bronze is universal podium grammar and
// reads instantly; the CHAMPION additionally wears the brand ribbon ring, so
// #1's identity cue is the logo melt while #2/#3 stay medal-metal.
function medalRing(rank: number) {
  return rank === 2 ? 'ring-2 ring-zinc-300' : rank === 3 ? 'ring-2 ring-amber-700' : 'ring-1 ring-black/10 dark:ring-white/10';
}

function maskName(rank: number) {
  // Filler only — no real name is sent for anonymous rows. Width varies per row
  // so blurred names look like distinct people, never a repeated block.
  return 'anonymouscreator'.slice(0, 6 + ((rank * 7) % 7));
}
function hrefFor(r: Row) {
  const sl = normalizeSlug(r.stageName || '');
  // /u/ resolves StageNameNormalized slugs, not raw names (broken links 07-10).
  return sl && sl.length >= 3 ? `/u/${sl}` : `/details/${r.personId}`;
}

export default function LeaderboardsBrowser({ initialAxis, isGuest = false }: { initialAxis: string; isGuest?: boolean }) {
  const [axis, setAxis] = useState(AXES.some((a) => a.value === initialAxis) ? initialAxis : 'views');
  const [board, setBoard] = useState<BoardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [myPersonId, setMyPersonId] = useState<number | null>(null);
  const [myPlacements, setMyPlacements] = useState<Placement[] | null>(null);
  const prefersReduced = useReducedMotion();
  const confettiFired = useRef<Set<string>>(new Set());

  // Hydrate the viewer's person id + their own placements (all axes in one
  // call). /me returns placements even for an anonymous top-10 ranker — that's
  // the conversion hook: "you're #3 but hidden, verify to claim it."
  useEffect(() => {
    if (isGuest) return;
    (async () => {
      try {
        const meRes = await apiFetch('/api/proxy/api/profile/me', undefined, { method: 'GET' });
        if (meRes.ok) {
          const me = (await meRes.json()) as { id?: number };
          if (me?.id) setMyPersonId(me.id);
        }
      } catch { /* silent */ }
      try {
        const plRes = await apiFetch('/api/proxy/api/leaderboards/me', { method: 'GET', cache: 'no-store' });
        if (plRes.ok) {
          const j = (await plRes.json()) as { placements?: Placement[] };
          setMyPlacements(Array.isArray(j.placements) ? j.placements : []);
        }
      } catch { /* silent */ }
    })();
  }, [isGuest]);

  // Fetch the board whenever the spec changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    (async () => {
      try {
        const qs = new URLSearchParams({ axis, scope: SCOPE, period: PERIOD }).toString();
        // 2-arg apiFetch (3-arg with undefined session DROPS init — see gotcha).
        // no-store: the proxy's catch-all Cache-Control pins success JSON for 1h.
        const res = await apiFetch(`/api/proxy/api/leaderboards?${qs}`, { method: 'GET', cache: 'no-store' });
        if (!res.ok) {
          // A failed request is an ERROR, not an empty board (the old code
          // rendered "No entries yet" on API failure — a fake empty state).
          if (!cancelled) setFailed(true);
          return;
        }
        const json = (await res.json()) as BoardResponse;
        if (!cancelled) {
          // Everyone who ranks is kept; non-identified rows render anonymously
          // (2026-07-12). Ties share a rank+medal (re-score 07-11); rows arrive
          // score-desc so equal scores are adjacent. Top 10 only (Jason).
          const incoming = (json.rows || []).slice(0, 10);
          let prevScore: number | null = null;
          let prevRank = 0;
          const rows = incoming.map((r, i) => {
            const rank = prevScore !== null && r.score === prevScore ? prevRank : i + 1;
            prevScore = r.score;
            prevRank = rank;
            return { ...r, rank };
          });
          setBoard({ ...json, rows });
        }
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [axis, reloadKey]);

  const switchAxis = (value: string) => {
    setAxis(value);
    // No querystring (Jason 2026-07-13): axis switch is pure client state; keep
    // the address bar a clean /leaderboards. Server still reads ?axis for
    // backward-compatible deep links; we just never write it.
    try {
      if (window.location.search) window.history.replaceState(null, '', '/leaderboards');
    } catch {}
  };

  const activeAxis = AXES.find((a) => a.value === axis) || AXES[0];
  // Memoized: `board?.rows || []` minted a fresh array every render, which
  // invalidated the standing and gap memos below on every single pass.
  const rows = useMemo(() => board?.rows ?? [], [board]);
  const showingEmpty = !loading && !failed && rows.length === 0;

  // Viewer's own standing on THIS axis. Identified rows match by personId; an
  // anonymous top-10 self is only knowable via /me (score match, name hidden).
  //
  // The rank is DERIVED FROM SCORE, never taken from /me. Two independent
  // divergences make the stored rank wrong for display: (1) the sweep stores
  // Rank = i + 1 with no tie collapsing while this component re-collapses ties,
  // so on the live post-likes board (10,8,8,7,7,6,6,5,5,5) stored ranks run 1-10
  // but displayed ranks are 1,2,2,4,4,6,6,8,8,8 — /me can return "#3" on a board
  // that shows no #3 at all; (2) GetBoardAsync filters suspended people at READ
  // time, so a dropped row leaves everyone below it with a stored rank one higher
  // than displayed. Counting rows that beat the viewer's score is correct under
  // both.
  //
  // rowIndex is what the "(you)" / "that's you" markers key off, and it is null
  // whenever the viewer can't be pinned to ONE row. Matching on rank alone put
  // the marker on every row sharing that rank — three anonymous rows share
  // displayed rank 8 on the live post-likes board, so up to three of them read
  // "that's you" at once.
  const myStanding = useMemo(() => {
    if (isGuest) return null;
    const p = myPlacements?.find((x) => x.axis === axis && x.scope === SCOPE && x.period === PERIOD);
    return deriveStanding(rows, myPersonId, p);
  }, [rows, myPlacements, myPersonId, axis, isGuest]);

  // How far the viewer is from the row above — the strongest true motivator on
  // this page, and the only personalized element that can render at all on a
  // fully anonymous board. Derived entirely from data already on the client.
  const myGap = useMemo(
    () => computeGap(rows, myStanding?.score ?? null),
    [rows, myStanding],
  );

  // Confetti when the viewer lands on a board where they hold a podium spot —
  // state-triggered (not on every load), once per axis per session, and never
  // under reduced-motion. This is the celebration payoff for competing.
  useEffect(() => {
    if (prefersReduced || !myStanding || myStanding.rank > 3) return;
    if (confettiFired.current.has(axis)) return;
    confettiFired.current.add(axis);
    let done = false;
    (async () => {
      try {
        const confetti = (await import('canvas-confetti')).default;
        if (done) return;
        confetti({
          particleCount: 80,
          spread: 70,
          startVelocity: 42,
          origin: { x: 0.5, y: 0.3 },
          colors: CONFETTI_COLORS,
          scalar: 0.9,
          disableForReducedMotion: true,
          zIndex: 60,
        });
      } catch { /* confetti is a nicety; never block the board */ }
    })();
    return () => { done = true; };
  }, [myStanding, axis, prefersReduced]);

  const maxScore = Math.max(1, ...rows.map((r) => r.score));
  // Bar width on a gentle power curve so distinct low scores separate instead
  // of clamping to a flat floor (re-score 07-11); equal scores share a width.
  const barWidth = (score: number) => Math.round(8 + 92 * Math.pow(Math.max(0, score) / maxScore, 0.6));

  // Podium = the top three SPOTS (array positions), NOT "rank <= 3": at this
  // volume ties are common (two rank-3s), and slotting by rank would blow the
  // podium out to four columns. Position drives layout; the medal shows the
  // real rank, so a tie still reads truthfully (a second #3 leads the list).
  const podium = rows.slice(0, 3);
  const rest = rows.slice(3);

  // On the live post-likes and gallery-likes boards NOT ONE row is identified —
  // every name is blurred, so the board reads as a wall of strangers with no
  // explanation unless you scroll past the whole thing to the footnote. When that
  // happens the footnote moves up under the podium, where the question is asked.
  const anyIdentified = rows.some((r) => !!r.stageName && r.isIdentified !== false);

  const visibilityNote = (
    <p className="text-xs text-muted-foreground">
      Everyone who ranks is listed. Verify your identity and opt in to show your name and link your profile; until then your name and photo stay blurred. Manage this in{' '}
      <Link href="/profile/messagepreferences" className="underline">Message preferences</Link>.
    </p>
  );

  const recounted = recountedPhrase(board?.computedAt);

  return (
    <div className="space-y-5">
      {/* Axis filter — active pill wears the ribbon fill (white label is AA on
          the magenta end); each pill carries its metric icon for fast scanning. */}
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Leaderboard type">
        {AXES.map((a) => {
          const on = axis === a.value;
          const Icon = a.Icon;
          return (
            <button
              key={a.value}
              onClick={() => switchAxis(a.value)}
              aria-pressed={on}
              type="button"
              className={
                'inline-flex items-center gap-1.5 rounded-full pl-3 pr-3.5 h-11 md:h-9 text-sm transition-colors ' +
                (on
                  ? 'btn-ribbon text-white font-semibold shadow-sm'
                  : 'border border-border text-muted-foreground hover:bg-accent/40 hover:text-foreground')
              }
            >
              <Icon className="h-4 w-4 shrink-0" strokeWidth={on ? 2.25 : 1.75} aria-hidden />
              {a.label}
            </button>
          );
        })}
      </div>

      {/* Legend. Teaches the gap ladder once so no per-row label is needed, and
          discloses that standings are a weekly snapshot — that disclosure is what
          licenses every "one like behind #4" on the page. The views board also
          states its mechanic, because it's the one metric creators assume they
          can drive directly and cannot. Guests get the anonymity explanation the
          member footnote gives members. */}
      {!failed && !showingEmpty && (
        <p className="text-xs text-muted-foreground">
          Ranked by <span className="font-medium text-foreground">{activeAxis.label.toLowerCase()}</span>, all-time. The podium is the top three; rows close to the one above show the gap.
          {activeAxis.mechanic ? ` ${activeAxis.mechanic}` : ''}
          {recounted ? ` Standings recount every Sunday; last recounted ${recounted}.` : ' Standings recount every Sunday.'}
          {isGuest && !anyIdentified && rows.length > 0 ? ' Names show once a creator verifies and opts in.' : ''}
        </p>
      )}

      {/* Your standing — the competitive hook. Only for members, only when we
          know where they stand on this axis. */}
      {!isGuest && !loading && !failed && myStanding && (
        <StandingStrip
          rank={myStanding.rank}
          identified={myStanding.identified}
          unit={activeAxis.unit}
          gap={myGap}
          hasScore={myStanding.score !== null && myStanding.score > 0}
        />
      )}

      {/* Not ranked here — the largest audience on the page, and until now the one
          told the least. One generic linkless sentence served all five boards; each
          board now names its own mechanic, gate, and destination. */}
      {!isGuest && !loading && !failed && !myStanding && rows.length > 0 && (
        <div className="flex items-start gap-3 rounded-[12px] border border-border bg-card px-4 py-3">
          <span className="ring-chip inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
            <activeAxis.Icon className="h-5 w-5" strokeWidth={2} aria-hidden />
          </span>
          <p className="text-sm text-foreground">
            {activeAxis.climbLead}{' '}
            <span className="text-muted-foreground">{activeAxis.climbSub}</span>{' '}
            <Link
              href={activeAxis.climbHref}
              onClick={() => trackEvent('leaderboard_climb_cta', { axis, cta: activeAxis.climbCta })}
              className="whitespace-nowrap font-medium text-foreground underline underline-offset-2"
            >
              {activeAxis.climbCta}
            </Link>
          </p>
        </div>
      )}

      {/* Board */}
      {loading ? (
        <BoardSkeleton />
      ) : failed ? (
        <div className="flex items-center justify-between gap-3 rounded-[12px] border border-border p-6 text-sm text-muted-foreground">
          <span>Couldn&apos;t load this board.</span>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-accent/40"
          >
            Retry
          </button>
        </div>
      ) : showingEmpty ? (
        <div className="rounded-[12px] border border-border p-6 text-sm text-muted-foreground">
          No entries on this board yet.
        </div>
      ) : (
        <div className="space-y-4">
          {/* Podium */}
          {podium.length > 0 && (
            <section
              aria-label="Top three"
              className="relative overflow-hidden rounded-[16px] border border-border bg-card px-3 pt-12 pb-5 sm:px-6 sm:pt-14"
            >
              {/* Rationed single glow behind the champion — low-alpha ribbon wash,
                  not a Vegas bloom. Decorative, aria-hidden, no contrast floor. */}
              <div
                aria-hidden
                className="pointer-events-none absolute left-1/2 top-0 h-40 w-40 -translate-x-1/2 -translate-y-14 rounded-full blur-3xl opacity-60"
                style={{ background: 'radial-gradient(closest-side, rgba(240,48,96,0.20), rgba(240,120,90,0.10), transparent)' }}
              />
              <ol className="relative flex items-end justify-center gap-2 sm:gap-5" aria-label="Podium, ranks one to three">
                {podium.map((r, i) => (
                  <PodiumColumn
                    key={r.personId ? `p${r.personId}` : `p-anon-${r.rank}-${i}`}
                    r={r}
                    position={i}
                    // Podium index maps 1:1 onto the rows array for i < 3.
                    isMe={myStanding?.rowIndex === i}
                    reduced={!!prefersReduced}
                  />
                ))}
              </ol>
            </section>
          )}

          {/* Nobody on this board is named: answer "why is everyone blurred?"
              here rather than below the whole board. Boards with named rows keep
              the note at the bottom, so this is invisible on views and videos. */}
          {!isGuest && !anyIdentified && visibilityNote}

          {/* Ranks 4–10 */}
          {rest.length > 0 && (
            <ol className="overflow-hidden rounded-[12px] border border-border divide-y divide-border" aria-label="Ranks four and below">
              {rest.map((r, i) => {
                const identified = !!r.stageName && r.isIdentified !== false;
                // rest[i] is rows[i + 3]; the row above it is rows[i + 2].
                const isMe = myStanding?.rowIndex === i + 3;
                const above = rows[i + 2];
                const gap = above ? above.score - r.score : null;
                const showGap = gap !== null && gap >= 0 && gap <= GAP_THRESHOLD;
                const rowClass =
                  'flex items-center gap-3 px-4 py-2.5 ' +
                  (identified ? 'hover:bg-accent/30 transition-colors' : 'cursor-default');
                const inner = (
                  <>
                    <span className="w-6 shrink-0 text-sm font-semibold tabular-nums text-muted-foreground">{r.rank}</span>
                    <span className={'relative h-9 w-9 shrink-0 overflow-hidden rounded-full bg-white/70 shadow-sm dark:bg-white/10 ' + medalRing(r.rank)}>
                      {identified ? (
                        <RowAvatar src={toAvatarUrl(r.avatarUrl || undefined)} alt={`${r.stageName} avatar`} sizes="36px" />
                      ) : (
                        <AnonAvatar />
                      )}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {identified ? (
                        <>
                          {r.stageName}
                          {isMe && <span className="ml-2 text-xs font-normal text-muted-foreground">(you)</span>}
                        </>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <span aria-hidden className="inline-block max-w-full select-none truncate align-middle text-foreground/50 blur-[4px]">{maskName(r.rank)}</span>
                          <span className="sr-only">Hidden creator, verify to reveal</span>
                          {isMe && <span className="ring-chip rounded-full px-2 py-0.5 text-[10px] font-semibold not-italic">that&apos;s you</span>}
                        </span>
                      )}
                    </span>
                    {/* The bar carried the exact absolute score in both title and
                        aria-label, so the no-raw-scores decision was skin-deep.
                        It now carries the GAP instead: more motivating and less
                        exposure. Not leak-free — barWidth still recovers
                        score/max to a point or two — but strictly less than
                        printing the integer. */}
                    <span className="w-20 shrink-0 sm:w-24">
                      <span className="block h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
                        <motion.span
                          className="block h-full origin-left rounded-full bg-[hsl(var(--primary))]"
                          style={{ width: `${barWidth(r.score)}%` }}
                          initial={prefersReduced ? false : { scaleX: 0 }}
                          animate={{ scaleX: 1 }}
                          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: prefersReduced ? 0 : 0.15 + i * 0.05 }}
                        />
                      </span>
                    </span>
                    {/* Sparse gap marker: the one affordance that makes a fully
                        anonymous board informative without touching identity.
                        Score is present on anonymous rows (the API nulls the name,
                        avatar and id, never the score), so this renders on the
                        post-likes and gallery-likes boards where nothing else can. */}
                    <span
                      className="w-14 shrink-0 text-right text-xs tabular-nums text-muted-foreground sm:w-16"
                      title={showGap ? gapPhrase(gap as number, above.rank, activeAxis.unit) : undefined}
                    >
                      {showGap ? (
                        <>
                          <span aria-hidden>{gap === 0 ? 'tied' : `${gap} behind`}</span>
                          <span className="sr-only">{gapPhrase(gap as number, above.rank, activeAxis.unit)}</span>
                        </>
                      ) : null}
                    </span>
                  </>
                );
                return (
                  <li
                    key={r.personId ? r.personId : `anon-${r.rank}-${i}`}
                    style={isMe ? { backgroundColor: 'color-mix(in oklab, hsl(var(--primary)) 9%, transparent)' } : undefined}
                  >
                    {identified ? (
                      <Link href={hrefFor(r)} className={rowClass}>{inner}</Link>
                    ) : (
                      <div className={rowClass} aria-label={`Rank ${r.rank}, hidden creator, verify to reveal`}>{inner}</div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}

      {/* Somewhere to GO. The board is otherwise a dead end for anyone who isn't
          ranked on it, and on the post-likes and gallery-likes boards it is a dead
          end for everyone: all ten rows are anonymous, so not one of them is even
          clickable. This points at the surface that actually moves the board,
          which closes the loop — viewers browse, creators gain, the board moves. */}
      {!failed && !showingEmpty && (
        <Link
          href={activeAxis.browseHref}
          onClick={() => trackEvent('leaderboard_browse_cta', { axis, cta: activeAxis.browseLabel })}
          className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {activeAxis.browseLabel}
          <ArrowUpRight className="h-4 w-4" strokeWidth={2} aria-hidden />
        </Link>
      )}

      {/* Members get the visibility footnote; guests get the join module from the
          page. On a board where nothing is identified it has already been shown
          above, directly under the podium. */}
      {!isGuest && anyIdentified && visibilityNote}
    </div>
  );
}

// ── Your-standing strip ──────────────────────────────────────────────────────
/**
 * The viewer's own standing, now carrying how far they are from the row above.
 *
 * Phrasing is deliberately NEUTRAL DISTANCE ("One like behind #6"), never agency
 * ("another like moves you to #6"). Two of the five metrics are purely other
 * people's clicks — views are counted when someone opens your profile, follows
 * when someone follows you — so promising a creator that N more moves them up
 * implies control the product doesn't give them. The action lives in a separate
 * CTA, on the axes where a real action exists.
 *
 * A precise gap is shown only when it's within GAP_THRESHOLD. Past that the
 * aspirational "N spots from the podium" reads better than a deflating true
 * number, and the split falls out of the data per axis: the tight boards (post
 * likes, gallery likes, follows) almost always show a gap, the wide ones (views,
 * video views) almost always show position.
 */
function StandingStrip({
  rank, identified, unit, gap, hasScore,
}: { rank: number; identified: boolean; unit: string[]; gap: GapInfo; hasScore: boolean }) {
  const onPodium = rank <= 3;
  const toPodium = rank - 3; // spots between the viewer and the podium
  const amount = (n: number) => (n === 1 ? `one ${unit[0]}` : `${n} ${unit[1]}`);

  // Suppressed when the viewer's score is 0: a creator can hold a board row on
  // content that is publicly invisible (a soft-flagged post stays IsActive but
  // sits in PendingReview), and "tied at #8" would be a strange thing to tell
  // someone whose work nobody can see.
  let gapClause: string | null = null;
  if (hasScore && gap) {
    if (gap.kind === 'behind' && gap.gap <= GAP_THRESHOLD) {
      gapClause = `${gapPhrase(gap.gap, gap.aboveRank, unit)}.`;
    } else if (gap.kind === 'lead' && gap.leadBy > 0) {
      gapClause = `You lead #${gap.belowRank} by ${amount(gap.leadBy)}.`;
    } else if (gap.kind === 'shared-lead') {
      gapClause = gap.tiedWith === 1 ? 'Tied with one other creator.' : `Tied with ${gap.tiedWith} other creators.`;
    }
  }

  const atTop = rank === 1 && !!gap && gap.kind !== 'behind';
  let icon = <Sparkles className="h-5 w-5" strokeWidth={2} aria-hidden />;
  let lead: ReactNode;
  let sub: ReactNode;

  if (onPodium && identified) {
    icon = <Crown className="h-5 w-5" strokeWidth={2} aria-hidden />;
    lead = atTop
      ? (gap?.kind === 'shared-lead' ? <>You share #1.</> : <>You hold #1.</>)
      : <>You&apos;re #{rank} — on the podium.</>;
    sub = gapClause ?? 'Defend your spot.';
  } else if (onPodium && !identified) {
    icon = <Crown className="h-5 w-5" strokeWidth={2} aria-hidden />;
    lead = <>You&apos;re #{rank} on this board — but hidden.</>;
    sub = (
      <>
        <Link href="/profile/messagepreferences" className="font-medium text-foreground underline underline-offset-2">Verify &amp; opt in</Link> to claim your name on the podium.
        {gapClause ? ` ${gapClause}` : ''}
      </>
    );
  } else if (identified) {
    lead = <>You&apos;re #{rank}.</>;
    sub = gapClause ?? (toPodium === 1 ? 'One spot from the podium.' : `${toPodium} spots from the podium.`);
  } else {
    // Anonymous rankers get the gap too, after the verify ask. They are 100% of
    // the live post-likes and gallery-likes boards, so withholding it would mean
    // the best motivator on the page never reaches anyone ranked on either.
    lead = <>You&apos;re #{rank} on this board — but hidden.</>;
    sub = (
      <>
        <Link href="/profile/messagepreferences" className="font-medium text-foreground underline underline-offset-2">Verify &amp; opt in</Link> to be listed by name.
        {gapClause ? ` ${gapClause}` : ''}
      </>
    );
  }

  return (
    <div className={'flex items-center gap-3 rounded-[12px] border px-4 py-3 ' + (onPodium ? 'border-transparent ring-chip' : 'border-border bg-card')}>
      <span
        className={'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ' + (onPodium ? 'text-[hsl(var(--primary))]' : 'ring-chip')}
        style={onPodium ? { backgroundColor: 'color-mix(in oklab, hsl(var(--primary)) 18%, transparent)' } : undefined}
      >
        {icon}
      </span>
      <p className="text-sm">
        <span className="font-js font-bold tabular-nums">{lead}</span>{' '}
        <span className="text-muted-foreground">{sub}</span>
      </p>
    </div>
  );
}

// ── Podium column ────────────────────────────────────────────────────────────
function PodiumColumn({ r, isMe, position, reduced }: { r: Row; isMe: boolean; position: number; reduced: boolean }) {
  const champ = position === 0;
  const identified = !!r.stageName && r.isIdentified !== false;
  // Layout is by PODIUM POSITION (0 = champion center, 1 = left, 2 = right),
  // decoupled from rank so a tie can't break the three-column shape; the medal
  // NUMBER below is still the creator's real rank.
  const orderClass = position === 0 ? 'order-2' : position === 1 ? 'order-1' : 'order-3';
  const avatarBox = champ ? 'h-20 w-20 sm:h-24 sm:w-24' : 'h-14 w-14 sm:h-16 sm:w-16';
  // Champion is raised; runners-up sit lower to imply a podium without a chunky
  // 3D block (which would read as arcade/casino — an explicit anti-reference).
  const lift = champ ? '-mt-2 sm:-mt-4' : 'mt-2';
  // Reveal order: runners-up first, champion lands last with a touch more pop.
  const delay = reduced ? 0 : champ ? 0.22 : 0.06 + position * 0.06;
  const badge = position === 0 ? 'bg-amber-400 text-black' : position === 1 ? 'bg-zinc-300 text-black' : 'bg-amber-700 text-white';
  const runnerRing = position === 1 ? 'ring-2 ring-zinc-300' : 'ring-2 ring-amber-700';

  const media = identified ? (
    <RowAvatar src={toAvatarUrl(r.avatarUrl || undefined)} alt={`${r.stageName} avatar`} sizes={champ ? '96px' : '64px'} />
  ) : (
    <AnonAvatar />
  );

  const avatar = (
    <span className={'relative block ' + lift}>
      {champ && (
        <Crown
          className="absolute -top-6 left-1/2 h-6 w-6 -translate-x-1/2 text-[hsl(var(--primary))] drop-shadow-sm sm:-top-7 sm:h-7 sm:w-7"
          strokeWidth={2}
          aria-hidden
          fill="rgba(236,186,106,0.9)"
        />
      )}
      {/* Champion wears the ribbon ring (the logo melt); runners-up wear medal metal. */}
      <span className={champ ? 'ribbon-ring block rounded-full shadow-md' : 'block rounded-full'}>
        <span className={'relative block overflow-hidden rounded-full bg-white/70 dark:bg-white/10 ' + avatarBox + (champ ? ' ring-1 ring-white/60 dark:ring-black/40' : ' ' + runnerRing)}>
          {media}
        </span>
      </span>
      {/* Rank medal, straddling the avatar base. */}
      <span className={'absolute -bottom-2 left-1/2 inline-flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full text-xs font-bold tabular-nums shadow ' + badge}>
        {r.rank}
      </span>
    </span>
  );

  const nameBlock = (
    <span className="mt-4 flex max-w-full flex-col items-center gap-1 text-center">
      {identified ? (
        <span className={'max-w-full truncate font-js font-bold leading-tight ' + (champ ? 'text-base sm:text-lg' : 'text-sm')}>
          {r.stageName}
          {isMe && <span className="ml-1 align-middle text-[10px] font-normal text-muted-foreground">(you)</span>}
        </span>
      ) : (
        <>
          <span aria-hidden className={'max-w-full select-none truncate blur-[4px] font-js font-bold text-foreground/50 ' + (champ ? 'text-base' : 'text-sm')}>{maskName(r.rank)}</span>
          <span className="sr-only">Rank {r.rank}, hidden creator, verify to reveal</span>
          <span className="ring-chip inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold">
            {isMe ? "that's you" : 'unclaimed'}
          </span>
        </>
      )}
    </span>
  );

  const body = (
    <motion.div
      className="flex w-full flex-col items-center"
      initial={reduced ? false : { opacity: 0, y: 14, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {avatar}
      {nameBlock}
    </motion.div>
  );

  // The <li> is the flex item: it carries the width + podium order; the inner
  // link/div fills it. Identified creators link to their profile; anonymous
  // rankers stay inert (no name, no link).
  const liClass = 'flex w-[30%] max-w-[8.5rem] justify-center ' + orderClass;
  if (identified) {
    return (
      <li className={liClass}>
        <Link href={hrefFor(r)} aria-label={`Rank ${r.rank}, ${r.stageName}`} className="group flex w-full justify-center">
          {body}
        </Link>
      </li>
    );
  }
  return (
    <li className={liClass} aria-label={`Rank ${r.rank}, hidden creator`}>
      {body}
    </li>
  );
}

// ── Skeleton ─────────────────────────────────────────────────────────────────
function BoardSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <p className="sr-only" aria-live="polite">Loading leaderboard…</p>
      <div className="rounded-[16px] border border-border bg-card px-3 pt-12 pb-5 sm:px-6 sm:pt-14">
        <div className="flex items-end justify-center gap-3 sm:gap-5">
          {[{ h: 'h-14 w-14 sm:h-16 sm:w-16', o: 'order-1 mt-2' }, { h: 'h-20 w-20 sm:h-24 sm:w-24', o: 'order-2 -mt-2 sm:-mt-4' }, { h: 'h-14 w-14 sm:h-16 sm:w-16', o: 'order-3 mt-2' }].map((s, i) => (
            <div key={i} className={'flex w-[30%] max-w-[8.5rem] flex-col items-center ' + s.o} aria-hidden>
              <div className={'rounded-full skeleton ' + s.h} />
              <div className="mt-4 h-3 w-16 rounded skeleton" />
            </div>
          ))}
        </div>
      </div>
      <div className="divide-y divide-border overflow-hidden rounded-[12px] border border-border" aria-hidden>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-2.5">
            <div className="h-4 w-5 rounded skeleton" />
            <div className="h-9 w-9 rounded-full skeleton" />
            <div className="h-4 max-w-[40%] flex-1 rounded skeleton" />
            <div className="ml-auto h-1.5 w-20 rounded skeleton sm:w-24" />
          </div>
        ))}
      </div>
    </div>
  );
}
